import { Fragment, useEffect, useState } from "react";
import { Toaster } from "@/components/ui/toaster";
import { ThemeProvider } from "next-themes";
import { startWebsiteAccessTracking } from '@/utils/websiteAccessTracker';
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { WebsiteFeedbackDialog } from "@/components/WebsiteFeedbackDialog";
import { supabase } from "@/integrations/supabase/client";
import Index from "./pages/Index";
import CreateJobPage from "./pages/CreateJobPage";
import { PaymentSuccess } from "./pages/PaymentSuccess";
import { AboutUs } from "./pages/AboutUs";
import { ForTradesmen } from "./pages/ForTradesmen";
import { SuccessStories } from "./pages/SuccessStories";
import { Pricing } from "./pages/Pricing";
import { WhyUs } from "./pages/WhyUs";
import { HowItWorks } from "./pages/HowItWorks";
import { HowItWorksCustomer } from "./pages/HowItWorksCustomer";
import { AdminDashboard } from "./pages/AdminDashboard";
import { Auth } from "./pages/Auth";
import TradesmanRegistration from "./pages/TradesmanRegistration";
import { TradesmanProfileSetupPage } from "./pages/TradesmanProfileSetup";
import TradesmanRegistrationSuccess from "./pages/TradesmanRegistrationSuccess";
import { PasswordReset } from "./pages/PasswordReset";
import JobPhotoManagement from "./pages/JobPhotoManagement";
import AuthCallback from "./pages/AuthCallback";
import NotFound from "./pages/NotFound";
import { PageLayout } from "./components/PageLayout";

// Import service pages
import { PlumbingPage } from "./pages/services/PlumbingPage";
import { ElectricalPage } from "./pages/services/ElectricalPage";
import { CarpentryPage } from "./pages/services/CarpentryPage";
import { HeatingPage } from "./pages/services/HeatingPage";
import { RoofingPage } from "./pages/services/RoofingPage";
import { PaintingPage } from "./pages/services/PaintingPage";
import { TilingPage } from "./pages/services/TilingPage";
import { LandscapingPage } from "./pages/services/LandscapingPage";

const queryClient = new QueryClient();

const LazyToasters = () => {
  const [mounted, setMounted] = useState(false);
  
  useEffect(() => {
    setMounted(true);
    // Start website access tracking when app loads
    startWebsiteAccessTracking();
  }, []);
  
  if (!mounted) return null;
  return (
    <> 
      <Toaster />
    </>
  );
};

const App = () => {
  const [showFeedbackDialog, setShowFeedbackDialog] = useState(false);

  useEffect(() => {
    // Start website access tracking on app mount (independent of toast providers)
    startWebsiteAccessTracking();

    const checkAndShowFeedback = async () => {
      // Check if user has already seen the feedback dialog in this session
      const hasSeenFeedback = sessionStorage.getItem('hasSeenFeedback');
      
      if (!hasSeenFeedback) {
        // Check if user is admin
        const { data: { user } } = await supabase.auth.getUser();
        
        if (user) {
          const { data: roleData } = await supabase
            .from('user_roles')
            .select('role')
            .eq('user_id', user.id)
            .eq('role', 'admin')
            .maybeSingle();
          
          // Don't show feedback to admins
          if (roleData) {
            return;
          }
        }
        
        // Show feedback dialog after 30 seconds for non-admins
        const timer = setTimeout(() => {
          setShowFeedbackDialog(true);
          sessionStorage.setItem('hasSeenFeedback', 'true');
        }, 30000);
        
        return () => clearTimeout(timer);
      }
    };

    checkAndShowFeedback();
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider attribute="class" defaultTheme="system" enableSystem>
        <BrowserRouter>
          <LazyToasters />
          <Routes>
            <Route path="/" element={<PageLayout><Index /></PageLayout>} />
            <Route path="/create-job" element={<PageLayout><CreateJobPage /></PageLayout>} />
            <Route path="/payment-success" element={<PageLayout><PaymentSuccess /></PageLayout>} />
            <Route path="/about" element={<PageLayout><AboutUs /></PageLayout>} />
            <Route path="/for-tradesmen" element={<PageLayout><ForTradesmen /></PageLayout>} />
            <Route path="/success-stories" element={<PageLayout><SuccessStories /></PageLayout>} />
            <Route path="/pricing" element={<PageLayout><Pricing /></PageLayout>} />
            <Route path="/why-us" element={<PageLayout><WhyUs /></PageLayout>} />
            <Route path="/how-it-works" element={<PageLayout><HowItWorks /></PageLayout>} />
            <Route path="/how-it-works-customer" element={<PageLayout><HowItWorksCustomer /></PageLayout>} />
            <Route path="/auth" element={<PageLayout><Auth /></PageLayout>} />
            <Route path="/tradesman-registration" element={<PageLayout><TradesmanRegistration /></PageLayout>} />
            <Route path="/tradesman-profile-setup" element={<PageLayout><TradesmanProfileSetupPage /></PageLayout>} />
            <Route path="/tradesman-registration-success" element={<PageLayout><TradesmanRegistrationSuccess /></PageLayout>} />
            <Route path="/auth/callback" element={<PageLayout><AuthCallback /></PageLayout>} />
            <Route path="/password-reset" element={<PageLayout><PasswordReset /></PageLayout>} />
            <Route path="/admin" element={<PageLayout><AdminDashboard /></PageLayout>} />
            <Route path="/job-photos" element={<PageLayout><JobPhotoManagement /></PageLayout>} />
            
            {/* Service Pages */}
            <Route path="/services/plumbing" element={<PageLayout><PlumbingPage /></PageLayout>} />
            <Route path="/services/electrical" element={<PageLayout><ElectricalPage /></PageLayout>} />
            <Route path="/services/carpentry" element={<PageLayout><CarpentryPage /></PageLayout>} />
            <Route path="/services/heating" element={<PageLayout><HeatingPage /></PageLayout>} />
            <Route path="/services/roofing" element={<PageLayout><RoofingPage /></PageLayout>} />
            <Route path="/services/painting" element={<PageLayout><PaintingPage /></PageLayout>} />
            <Route path="/services/tiling" element={<PageLayout><TilingPage /></PageLayout>} />
            <Route path="/services/landscaping" element={<PageLayout><LandscapingPage /></PageLayout>} />
            
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<PageLayout><NotFound /></PageLayout>} />
          </Routes>
        </BrowserRouter>
        <WebsiteFeedbackDialog 
          open={showFeedbackDialog} 
          onOpenChange={setShowFeedbackDialog}
        />
      </ThemeProvider>
    </QueryClientProvider>
  );
};

export default App;
